// Payouts → Statements — who we owe, why, and the one act that pays them.
//
// ── CONFIRM IS GONE (founder, 24 Sep 2026) ─────────────────────────────────
// "NOthing on button press … if there is any infra as such in iot admin or its
// V2, lets remove that leagacy too and refactor basis our new design
// decisions." A statement is no longer a proposal awaiting a signature: it is
// the payout engine's own working, 'open' until money moves against it. What a
// media owner is owed is now said by a DOCUMENT — the self-billed tax invoice
// raised in their name at the tail of the month close — not by a person
// clicking Confirm here. Measured before removing it: no statement has ever
// carried a confirmed_at.
//
// MARK PAID STAYS, and is the only act on this screen. It is not legacy: it is
// how a real bank transfer is recorded against a period, and money will move.
// The TDS refusal moved WITH it, server-side, because what that check protects
// is money leaving with the wrong tax withheld — not a screen gesture.
//
// WHAT THIS SCREEN IS NOW: a read-only window onto the engine's working, plus
// that one act. It is the only place in the estate that renders gated-away, SIM
// and carry as a legible waterfall, which is exactly where this design leaves
// those numbers — off the invoice, on the payment plane, still auditable.
//
// Every statement renders as ONE MONEY FLOW, not a row of columns: what the
// fleet's screens EARNED, split into where it went — net to the party, SIM
// recovered, TDS withheld, and what the day-gate held back. Earned − gate =
// payable; payable − SIM − TDS ± adjustments + carry = net. Both the gated
// and ungated numbers are first-class (founder, 3 Sep).
//
// NOTHING IS COMPUTED HERE beyond display sums of served paise. Numbers come
// from billing-service via the thin proxy; the pipeline strip shows what the
// machine has done and lets you run its stages by hand — the same handlers
// the daily crons invoke.

import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { PageHead, Loading, ErrorState, Empty, Pill, Button } from '../../components/ui';
import useApi from '../../lib/useApi';
import api from '../../../services/api';
import { inr, monthLabel, STATUS_TONE, ROLE_LABEL, BASE, shaped } from './lib';
import Explainer from './Explainer';
import s from './Payouts.module.css';

const EXPLAIN = {
  owed: 'Net payable across every open statement — what the engine computed and nothing has paid. '
    + 'It is the payment plane\'s own working, not what any invoice says: a media owner\'s self-billed '
    + 'tax invoice states the presence accrual, which is a different and smaller number, on purpose.',
  earned: 'What partner fleets’ screens earned before the day-gate spoke: payable + gated-away, '
    + 'summed over every non-void statement. The ungated number, kept deliberately (founder, 3 Sep).',
  gated: 'Withheld by the per-day eligibility gate — days that missed the trips/hours bar. '
    + 'Informational and auditable per day on the Eligibility screen; it is never paid and never silently hidden.',
  sim: 'Per-device SIM cost recovered from premise operators — UNGATED (a parked day still burns its SIM) '
    + 'and deducted before the TDS base. ₹170/device/month today; a term on the fleet’s agreement.',
  pipeFrozen: 'The latest IST day whose trips/hours are frozen. Frozen once, never recomputed — '
    + 'a mart re-derive can never move money.',
  pipeInvoices: 'Settled advertiser invoices whose money has been split into share lines. '
    + 'The base is exactly what settlement collected — reproduced to the paise, or the sweep aborts.',
  pipeOracle: 'The standing reconciler: every invoice’s lines must sum back to its base exactly, '
    + 'dispositions must be consistent, statements must equal their consumed lines. Green or paged.',
  legNet: 'What actually pays out after SIM, TDS, adjustments and carry.',
  legSim: 'SIM recovery — ungated, pre-TDS.',
  legTds: 'Withheld tax, per the party’s agreement rates (194C/194H by leg).',
  legGated: 'Held back by the day-gate. Every gated day is listed with its numbers on the Eligibility screen.',
};

const JOBS = [
  { id: 'snapshot', label: 'Freeze yesterday', title: 'Freeze trips & hours per device-day from the signals mart (self-backfills missed days)' },
  { id: 'sweep', label: 'Split settled money', title: 'Compute share lines for invoices settled before today (exact to the paise or it aborts)' },
  { id: 'close', label: 'Month close', title: 'Open one statement per party for the previous month, and raise each media owner\'s self-billed tax invoice' },
];

function MoneyFlow({ st }) {
  const gross = Number(st.gross_share_paise);
  const gated = Number(st.gated_away_paise);
  const sim = Number(st.sim_expense_paise);
  // TWO TDS FACTS, AND ONLY ONE OF THEM IS EVER A MEASUREMENT.
  // tds_paise is the ENGINE's expectation, NOT NULL DEFAULT 0 — so "nobody
  // computed a withholding" and "the withholding is zero" are the same value,
  // and every statement in production carries 0 while its own notes say no rate
  // is configured. Rendering that as a waterfall leg is silence wearing a
  // number. tds_withheld_paise (V362) is what the accounts team STATED when
  // they made the transfer, and it is NULL until they do.
  const stated = st.tds_withheld_paise == null ? null : Number(st.tds_withheld_paise);
  const tds = stated != null ? stated : Number(st.tds_paise);
  const tdsIsMeasured = stated != null;
  const net = Number(st.net_payable_paise);
  const carryOut = Number(st.carry_out_paise);
  const earned = gross + gated;
  const total = Math.max(earned, 1);
  const w = (v) => ({ width: `${Math.max((v / total) * 100, v > 0 ? 1.5 : 0)}%` });
  // the flow bar splits EARNED into where it went; the residue of payable
  // after sim+tds is the net (or carry, when below the ₹1,000 floor)
  const netish = Math.max(gross - sim - tds, 0);

  return (
    <>
      <div className={s.flowBar} aria-hidden="true">
        <span className={s.segNet} style={w(netish)} />
        <span className={s.segSim} style={w(sim)} />
        <span className={s.segTds} style={w(tds)} />
        <span className={s.segGated} style={w(gated)} />
      </div>
      <div className={s.flowLegend}>
        <span className={`${s.leg} ${s.legMuted}`} title={EXPLAIN.earned}>
          <span className={s.legLabel}>Earned (ungated)</span>
          <span className={s.legValue}>{inr(earned)}</span>
        </span>
        <span className={s.leg} title={EXPLAIN.legGated}>
          <span className={s.legLabel}><i className={`${s.legDot} ${s.segGated}`} />Gate withheld</span>
          <span className={s.legValue}>−{inr(gated)}</span>
        </span>
        <span className={`${s.leg} ${s.legMuted}`} title="Earned minus the gate: the month’s payable earnings.">
          <span className={s.legLabel}>Payable</span>
          <span className={s.legValue}>{inr(gross)}</span>
        </span>
        <span className={s.leg} title={EXPLAIN.legSim}>
          <span className={s.legLabel}><i className={`${s.legDot} ${s.segSim}`} />SIM</span>
          <span className={s.legValue}>−{inr(sim)}</span>
        </span>
        {/* Shown only when it is a stated fact or an actual deduction. An
            unpaid statement whose rate nobody configured renders NOTHING here
            rather than a confident −₹0.00. */}
        {(tdsIsMeasured || tds > 0) && (
          <span className={s.leg} title={tdsIsMeasured ? (st.tds_basis || EXPLAIN.legTds) : EXPLAIN.legTds}>
            <span className={s.legLabel}><i className={`${s.legDot} ${s.segTds}`} />TDS</span>
            <span className={s.legValue}>−{inr(tds)}</span>
          </span>
        )}
        {/* "Net payable" was the last place this number was presented as money
            owed. It is the ledger's residue after SIM, TDS and carry — a
            collection-side figure. What is owed is the invoice, stated in words
            under this bar. */}
        <span className={s.leg} title={EXPLAIN.legNet}>
          <span className={s.legLabel}><i className={`${s.legDot} ${s.segNet}`} />Ledger residue</span>
          <span className={s.legValue}>{inr(net)}</span>
        </span>
        {carryOut !== 0 && (
          <span className={`${s.leg} ${s.legMuted}`} title="Below the ₹1,000 floor (or a negative month) rides forward to the next statement.">
            <span className={s.legLabel}>Carries forward</span>
            <span className={s.legValue}>{inr(carryOut)}</span>
          </span>
        )}
      </div>
    </>
  );
}

function Composition({ id }) {
  const { data, error, loading } = useApi(
    () => api.get(`${BASE}/statements/${id}`).then((r) => shaped(r.data, 'statement')), [id]);
  if (loading) return <Loading what="the composition" />;
  if (error) return <ErrorState error={error} />;
  const roles = {};
  for (const l of data.lines || []) roles[l.role] = (roles[l.role] || 0) + Number(l.share_paise);
  return (
    <div className={s.compGrid}>
      {Object.entries(roles).map(([role, paise]) => (
        <div className={s.kv} key={role}>
          <div className="k">{ROLE_LABEL[role] || role}</div>
          <div className="v">{inr(paise)}</div>
        </div>
      ))}
      {(data.adjustments || []).map((a, i) => (
        <div className={s.kv} key={`adj${i}`}>
          <div className="k">Adjustment · {a.reason}</div>
          <div className="v">{inr(a.amount_paise)}</div>
        </div>
      ))}
    </div>
  );
}

// 'open' is the live state (V360/V361); 'draft' is read alongside it for the
// life of the enum so this screen is correct on either side of the deploy.
const isOpen = (st) => st.status === 'open' || st.status === 'draft';

function StatementCard({ st, onChanged }) {
  const [openComp, setOpenComp] = useState(false);
  const [paying, setPaying] = useState(false);
  const [payRef, setPayRef] = useState('');
  const [tdsRs, setTdsRs] = useState('');
  const [tdsWhy, setTdsWhy] = useState('');
  const [note, setNote] = useState(null);

  const act = async (action, body = {}) => {
    setNote(null);
    try {
      await api.post(`${BASE}/statements/${st.id}/${action}`, body);
      setPaying(false);
      onChanged();
    } catch (e) {
      setNote(e?.response?.data?.details || e?.response?.data?.error || e.message);
    }
  };

  return (
    <div className={s.card}>
      <div className={s.cardHead}>
        <span className={s.cardTitle}>{st.party_name}</span>
        <span className={s.cardWhen}>{monthLabel(st.period_month)}</span>
        <Pill tone={STATUS_TONE[st.status]}>{st.status}</Pill>
        <span className={s.cardActions}>
          <Button onClick={() => setOpenComp((v) => !v)}>{openComp ? 'Hide detail' : 'Detail'}</Button>
          {/* NOTHING IS PAID AGAINST NO DOCUMENT. That is the whole of the
              ₹958.60 problem: remit the engine's net against an invoice for a
              different figure and the difference moves with no paperwork behind
              it — no input tax credit for us, an unexplained receipt in their
              books, two ledgers that never reconcile. So the act is denominated
              in the invoice and is absent until there is one. */}
          {isOpen(st) && !paying && Number(st.invoiced_documents) > 0 && (
            <Button primary onClick={() => setPaying(true)} title={`Record a bank transfer of ${inr(st.invoiced_total_paise)} to ${st.party_name}`}>
              Mark paid…
            </Button>
          )}
        </span>
      </div>

      <MoneyFlow st={st} />

      {/* WHAT IS OWED, AND WHAT THE BAR ABOVE IS. The waterfall is the share
          ledger's collection-side working — the founder's "legacy bull", kept
          because gated_away is the auditable record of what the gate withheld
          and the consumption link is the close's only anti-double-count. It is
          NOT what we owe. This line is. */}
      {Number(st.invoiced_documents) > 0 ? (
        <div className={s.hint}>
          <b>Owed: {inr(st.invoiced_total_paise)}</b> — {inr(st.invoiced_taxable_paise)} for the supply plus tax,
          {' '}invoiced in {st.party_name}’s name as <b>{st.invoice_number}</b>
          {st.invoiced_documents > 1 ? ` and ${st.invoiced_documents - 1} correction(s)` : ''}
          {st.invoice_issued_on ? ` on ${st.invoice_issued_on}` : ''}.
          {' '}The bar above is the share ledger, not the amount payable. Nothing has been remitted.
        </div>
      ) : (
        <div className={s.hint}>
          {st.invoice_block_sentence
            ? <>Not invoiced yet — {st.invoice_block_sentence}</>
            : <>Not invoiced yet. The month close raises the document once the month is judged, or records why it could not. Nothing is payable until it does.</>}
        </div>
      )}

      {/* THE TDS BANNER IS GONE WITH THE BUTTON IT GUARDED, and so is the
          string-sniff that drove it — a UI branch keyed on substring-matching a
          free-text notes column was fragile independently of this change. The
          refusal itself did not lift: it is a server precondition on mark-paid
          now, where money actually leaves, and it answers with its own sentence
          into the banner below. */}

      {paying && (
        <div className={s.inlineConfirm}>
          Record the transfer of <b>{inr(st.invoiced_total_paise)}</b> to <b>{st.party_name}</b> for {monthLabel(st.period_month)}
          {' '}— the invoiced total, tax included:
          <input value={payRef} onChange={(e) => setPayRef(e.target.value)} placeholder="Bank/UTR reference, e.g. UTR N123456789" />
          {/* THE WITHHOLDING IS STATED HERE, BY THE PERSON MAKING THE TRANSFER
              (founder, 24 Sep: "Its for the accounts team to for now manage the
              accounting liability during transactions being triggered"). Zero
              is allowed; leaving it blank is not. No rate is configured for any
              party, so the engine has nothing to offer and does not pretend to. */}
          <input value={tdsRs} onChange={(e) => setTdsRs(e.target.value)} placeholder="TDS withheld ₹ (0 if none)" inputMode="decimal" />
          <input value={tdsWhy} onChange={(e) => setTdsWhy(e.target.value)} placeholder="Section and rate applied, e.g. 194C at 1%" />
          <Button
            primary
            onClick={() => act('mark-paid', {
              payment_reference: payRef,
              tds_withheld_paise: Math.round(Number(tdsRs) * 100),
              tds_basis: tdsWhy,
            })}
            disabled={!payRef.trim() || tdsRs.trim() === '' || !Number.isFinite(Number(tdsRs)) || Number(tdsRs) < 0 || !tdsWhy.trim()}
          >
            Mark paid
          </Button>
          <Button onClick={() => setPaying(false)}>Cancel</Button>
        </div>
      )}

      {note && <div className={s.badBanner}>{note}</div>}
      {openComp && <Composition id={st.id} />}
    </div>
  );
}

function Pipeline({ ov, oracle, onRan }) {
  const [busy, setBusy] = useState(null);
  const [outcome, setOutcome] = useState(null);

  const run = async (job) => {
    setBusy(job.id);
    setOutcome(null);
    try {
      const r = await api.post(`${BASE}/run/${job.id}`, {});
      const res = r.data?.result || {};
      const text = job.id === 'snapshot' ? `froze ${res.frozen ?? 0} device-days`
        : job.id === 'sweep' ? `${res.shared ?? 0} invoices split, ${res.deferred ?? 0} deferred`
        : `${res.drafted ?? 0} statement(s) opened`
          + (res.selfBilled ? `, ${(res.selfBilled.issued || []).length + (res.selfBilled.supplementary || []).length} invoice(s) raised`
            + ((res.selfBilled.blocked || []).length ? `, ${res.selfBilled.blocked.length} blocked` : '') : '');
      setOutcome({ ok: true, text: `${job.label}: ${text}.` });
      onRan();
    } catch (e) {
      setOutcome({ ok: false, text: `${job.label} failed: ${e?.response?.data?.details || e?.response?.data?.error || e.message}` });
    } finally {
      setBusy(null);
    }
  };

  const invShared = ov?.invoices?.find((i) => i.status === 'shared');

  return (
    <>
      <div className={s.pipeline}>
        <span className={s.pipeTitle}>The machine</span>
        <span className={s.pipeItem} title={EXPLAIN.pipeFrozen}>
          frozen through <b>{ov?.eligibility?.last_frozen_day || '—'}</b>
        </span>
        <span className={s.pipeArrow}>→</span>
        <span className={s.pipeItem} title={EXPLAIN.pipeInvoices}>
          <b>{invShared ? Number(invShared.n).toLocaleString('en-IN') : 0}</b> invoices split · <b>{invShared ? inr(invShared.base_paise, 0) : '₹0'}</b>
        </span>
        <span className={s.pipeArrow}>→</span>
        <span className={s.pipeItem} title={EXPLAIN.pipeOracle}>
          oracle {oracle == null ? '…' : oracle.clean
            ? <b style={{ color: 'var(--grow-text)' }}>clean ✓</b>
            : <b style={{ color: 'var(--bad-text)' }}>{oracle.breaches.length} breach(es)</b>}
        </span>
        <span className={s.pipeActions}>
          {JOBS.map((j) => (
            <Button key={j.id} onClick={() => run(j)} disabled={!!busy} title={j.title}>
              {busy === j.id ? 'running…' : j.label}
            </Button>
          ))}
        </span>
      </div>
      {outcome && <div className={outcome.ok ? s.okBanner : s.badBanner}>{outcome.text}</div>}
      <div className={s.hint} style={{ margin: 'calc(-1 * var(--s3)) 0 var(--s4) var(--s2)' }}>
        Left to right, what the crons do daily: freeze yesterday’s trips &amp; hours per screen → split newly settled
        advertiser money into share lines → (monthly) open one statement per party and raise each media
        owner’s tax invoice. Watch it accumulate on{' '}
        <NavLink to="/v2/payouts/split">The split</NavLink>.
      </div>
    </>
  );
}

export default function Statements() {
  const overview = useApi(() => api.get(`${BASE}/overview`).then((r) => shaped(r.data, 'eligibility')), []);
  const oracle = useApi(() => api.get(`${BASE}/oracle`).then((r) => shaped(r.data, 'clean')), []);
  const { data, error, loading, reload } = useApi(
    () => api.get(`${BASE}/statements`).then((r) => shaped(r.data, 'statements')), []);

  const reloadAll = () => { reload(); overview.reload(); oracle.reload(); };

  const head = (
    <PageHead
      title="Payouts"
      sub="Supply-side settlement, one statement per party per month. The close opens each statement and raises each media owner’s tax invoice; a human records the bank transfer. Nothing here is approved by hand."
    />
  );

  if (loading || overview.loading) return <div className={s.wrap}>{head}<Loading what="payout statements" /></div>;
  if (error) return <div className={s.wrap}>{head}<ErrorState error={error} onRetry={reloadAll} /></div>;

  const rows = (data?.statements || []).filter((r) => r.status !== 'void');
  // ── WHAT WE OWE IS WHAT WE INVOICED ──────────────────────────────────────
  // Not net_payable_paise. That is the engine's collection-side working — the
  // founder's "legacy bull" (24 Sep) — and 90% of the base behind it was
  // priced under the blanket rule, before any passenger test existed. The
  // document raised in the media owner's name states the presence accrual,
  // which is what they supplied, and it is the only figure either side can
  // reconcile against a tax return.
  const owed = rows.filter(isOpen).reduce((a, r) => a + Number(r.invoiced_total_paise || 0), 0);
  const awaitingDoc = rows.filter((r) => isOpen(r) && !Number(r.invoiced_documents));

  return (
    <div className={s.wrap}>
      {head}

      <div className={s.hero}>
        <div className={`${s.heroCard} ${s.heroPrimary}`} title={EXPLAIN.owed}>
          <span className={s.heroLabel}>Invoiced, unpaid</span>
          <span className={s.heroValue}>{inr(owed)}</span>
          {/* NOT "awaiting your confirm" — there is nothing to confirm; and
              not the engine's net, which is a different and larger number for a
              reason the card below spells out. A month with no document owes
              nothing YET, and the strip says so rather than filling the gap. */}
          <span className={s.heroSub}>
            {awaitingDoc.length
              ? `${awaitingDoc.length} month(s) not yet invoiced`
              : (() => { const n = rows.filter(isOpen).length; return n === 1 ? '1 invoice unpaid' : `${n} invoices unpaid`; })()}
          </span>
        </div>
        <div className={s.heroCard} title={EXPLAIN.earned}>
          <span className={s.heroLabel}>Share ledger, ungated</span>
          <span className={s.heroValue}>{inr(rows.reduce((a, r) => a + Number(r.gross_share_paise) + Number(r.gated_away_paise), 0))}</span>
          <span className={s.heroSub}>the collection-side working, not what we owe</span>
        </div>
        <div className={s.heroCard} title={EXPLAIN.gated}>
          <span className={s.heroLabel}>Gate withheld</span>
          <span className={s.heroValue}>{inr(rows.reduce((a, r) => a + Number(r.gated_away_paise), 0))}</span>
          <span className={s.heroSub}>days under the trips/hours bar — see Eligibility</span>
        </div>
        <div className={s.heroCard} title={EXPLAIN.sim}>
          <span className={s.heroLabel}>SIM recorded</span>
          <span className={s.heroValue}>{inr(rows.reduce((a, r) => a + Number(r.sim_expense_paise), 0))}</span>
          {/* RECORDED, NOT CHARGED (founder, 24 Sep): "invoicing is agnostic to
              SIM cost… no booking and recovering cycle now". It stays on the
              ledger so the day it IS charged the change is a new outward
              document, never a restatement of one already filed. */}
          <span className={s.heroSub}>₹170 / device / month · recorded, not charged</span>
        </div>
      </div>

      <Pipeline ov={overview.data} oracle={oracle.data} onRan={reloadAll} />

      {rows.length === 0 ? (
        <Empty title="No statements yet">
          The month close opens one statement per party on the 2nd. To do it now: run the three
          machine stages above, left to right — freeze, split, close. Nothing is paid until a human
          records the bank transfer.
        </Empty>
      ) : (
        rows.map((st) => <StatementCard key={st.id} st={st} onChanged={reloadAll} />)
      )}
      <Explainer terms={['statement', 'earned', 'gate', 'payable', 'sim', 'tds', 'carry', 'settled']} />
    </div>
  );
}
